import React, { useMemo } from 'react';
import { DownloadIcon, MailIcon } from 'lucide-react';
import { saleTermsMeta, saleTermsSections } from '../data/termsOfSale';
import { PolicyContents } from '../components/legal/PolicyContents';
import { PolicySectionBody } from '../components/legal/PolicySectionBody';
import { BackToTop } from '../components/legal/BackToTop';
import { SiteFooter } from '../components/ui/SiteFooter';
import { SiteHeader } from '../components/ui/SiteHeader';
import { useActiveSection } from '../hooks/useActiveSection';
const metaLinkStyles = 'inline-flex items-center gap-2 text-sm font-medium text-purple-700 underline underline-offset-4 decoration-purple-300 hover:text-purple-900 hover:decoration-purple-700 transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-ivory rounded';
export function TermsOfSalePage() {
  const sectionIds = useMemo(() => saleTermsSections.map(section => section.id), []);
  const activeId = useActiveSection(sectionIds);
  return <div className="min-h-screen bg-ivory font-sans text-charcoal selection:bg-purple-200 selection:text-charcoal" data-id="element-1110" data-mp="10i5bzkdcecnq">
      {/* Navigation */}
      <SiteHeader currentPath="/terms-of-sale" data-id="element-1111" data-mp="mjp964ilp6av" />

      <main className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1112" data-mp="qjzf5h6e7wpq">
        {/* Title area */}
        <header className="pt-16 md:pt-24 pb-10 md:pb-14 border-b border-charcoal/10" data-id="element-1113" data-mp="qm25617jppug">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 mb-5" data-id="element-1114" data-mp="1eabsj7keqlnp">
            {saleTermsMeta.company}
          </p>
          <h1 className="max-w-[20ch] font-serif text-4xl md:text-6xl text-charcoal leading-[1.05] tracking-tight" data-id="element-1115" data-mp="15i1k9vt2oq93">
            {saleTermsMeta.title}
          </h1>
          <p className="mt-6 max-w-[62ch] text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-1116" data-mp="19gh3dom19eh8">
            {saleTermsMeta.summary}
          </p>

          <dl className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm" data-id="element-1117" data-mp="22065rq8n2c8h">
            <div className="flex items-baseline gap-2" data-id="element-1118" data-mp="2bvehezpshqoe">
              <dt className="text-charcoal/50" data-id="element-1119" data-mp="2bt0l6njdi8f9">Version</dt>
              <dd className="font-medium text-charcoal" data-id="element-1120" data-mp="27ukqdxh1vvra">{saleTermsMeta.version}</dd>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-1121" data-mp="22t57oc2ci1c2">
              <dt className="text-charcoal/50" data-id="element-1122" data-mp="1xvbzxele8z9o">Last updated</dt>
              <dd className="font-medium text-charcoal" data-id="element-1123" data-mp="1xv3prugno826">{saleTermsMeta.lastUpdated}</dd>
            </div>
          </dl>

          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-1124" data-mp="1sy3khlrmr9c4">
            <a href={saleTermsMeta.pdfUrl} target="_blank" rel="noopener noreferrer" className={metaLinkStyles} data-id="element-1125" data-mp="1o4916jfxswdk">
              <DownloadIcon aria-hidden="true" className="w-4 h-4" data-id="element-1126" data-mp="1k1ugdtspjmjn" />
              Download the PDF
            </a>
            <a href={`mailto:${saleTermsMeta.email}`} className={metaLinkStyles} data-id="element-1127" data-mp="1a5rhcwt5k40x">
              <MailIcon aria-hidden="true" className="w-4 h-4" data-id="element-1128" data-mp="1a7vnlleixf4d" />
              {saleTermsMeta.email}
            </a>
          </div>
        </header>

        {/* Index + body */}
        <div className="lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-14 xl:gap-20 pb-24 md:pb-32" data-id="element-1129" data-mp="v27vafnyiank">
          <PolicyContents sections={saleTermsSections} activeId={activeId} label="Terms and Conditions of Sale sections" data-id="element-1130" data-mp="zzzp5f85w6ue" />

          <article className="max-w-[68ch] pt-10 lg:pt-14" data-id="element-1131" data-mp="14z2wx7fj0r62">
            <div className="pb-12 space-y-5" data-id="element-1132" data-mp="19yp5h3ketj8j">
              {saleTermsMeta.preamble.map(paragraph => <p key={paragraph.slice(0, 40)} className="text-base text-charcoal/80 leading-[1.8]" data-id="element-1133" data-mp="cdt4nthjwyna">
                  {paragraph}
                </p>)}
            </div>

            <div className="divide-y divide-charcoal/10 border-t border-charcoal/10" data-id="element-1134" data-mp="m9nvvczuc21o">
              {saleTermsSections.map(section => <PolicySectionBody key={section.id} section={section} data-id="element-1135" data-mp="r2z7drfll6zx" />)}
            </div>

            <p className="pt-12 text-sm text-charcoal/50" data-id="element-1136" data-mp="3uq96szc1957f">
              © 2026 Fem Entity Inc. All rights reserved. Version {saleTermsMeta.version}, last updated{' '}
              {saleTermsMeta.lastUpdated}.
            </p>
          </article>
        </div>
      </main>

      <BackToTop data-id="element-1137" data-mp="1vcr3qlup4agu" />

      <SiteFooter currentPath="/terms-of-sale" data-id="element-1138" data-mp="1v93cxugmy25t" />
    </div>;
}